import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi, ErreurApi, EVENEMENT_SESSION_EXPIREE, urlApi } from './api';
import { TOUS_LES_SITES } from '../site/context';

/**
 * Espace de conformité Qualiopi (TF-431) : les indicateurs du référentiel
 * national qualité (33 depuis le décret n° 2026-728, TF-471), ce que la
 * plateforme en contrôle et les preuves déposées.
 * Statut, manques et refus des gestes viennent de l'API
 * (`domain/conformite-qualiopi.ts`) : l'écran affiche, il ne décide rien.
 */

export type StatutIndicateur = 'CONFORME' | 'A_COMPLETER' | 'NON_APPLICABLE';
export type EtatControle = 'SATISFAIT' | 'MANQUANT' | 'INFORMATION';
export type EtatPreuve = 'VALABLE' | 'EXPIREE' | 'RETIREE';

export interface LienQualiopi {
  libelle: string;
  /** `null` : écran fermé au lecteur, le lien reste un texte. */
  chemin: string | null;
  externe: boolean;
}

export interface ControleQualiopi {
  code: string;
  titre: string;
  etat: EtatControle;
  constat: string;
  manques: { libelle: string; lien: LienQualiopi | null }[];
  autresManques: number;
  sources: LienQualiopi[];
}

export interface ExigencePreuve {
  code: string;
  libelle: string;
  aide: string;
  remplie: boolean;
  manque: string | null;
  preuves: string[];
}

export interface IndicateurQualiopi {
  numero: number;
  critere: number;
  titre: string;
  enonce: string;
  portee: { libelle: string; conditionnelle: boolean };
  /** Exigence du texte pas encore demandée, faute de l'arrêté qui fixe son seuil. */
  observation: string | null;
  statut: StatutIndicateur;
  statutLibelle: string;
  ton: string;
  manques: string[];
  controles: ControleQualiopi[];
  exigences: ExigencePreuve[];
  preuves: string[];
  nonApplicable: { motif: string; declareLe: string; declarePar: string | null } | null;
  refusDeclaration: string | null;
}

export interface PreuveQualiopi {
  id: string;
  titre: string;
  description: string | null;
  support: 'FICHIER' | 'LIEN';
  nomFichier: string | null;
  tailleOctets: number | null;
  url: string | null;
  dateDocument: string;
  valableJusquAu: string | null;
  site: { code: string; nom: string } | null;
  deposeLe: string;
  deposePar: string | null;
  etat: EtatPreuve;
  etatLibelle: string;
  ton: string;
  retrait: { le: string; par: string | null; motif: string } | null;
  rattachements: { indicateur: number; attendu: string | null; attenduLibelle: string | null }[];
  refusRetrait: string | null;
}

export interface CertificationQualiopi {
  etat: 'CERTIFIE' | 'A_VERIFIER' | 'NON_RELEVE';
  libelle: string;
  ton: string;
  organismes: {
    numeroDeclaration: string | null;
    antennes: string[];
    certifie: boolean;
    categories: { libelle: string; certifiee: boolean }[];
    releveLe: string | null;
    detail: string;
  }[];
  echeance: { intitule: string; nature: string; date: string; jours: number; libelle: string; ton: string } | null;
  evolutionsAAnalyser: number;
}

export interface ReponseQualiopi {
  calculeLe: string;
  sites: { code: string; nom: string }[];
  certification: CertificationQualiopi;
  /** Texte qui fixe la rédaction des indicateurs. */
  version: { texte: string; enVigueurLe: string; lien: string };
  synthese: {
    indicateurs: number;
    applicables: number;
    conformes: number;
    aCompleter: number;
    nonApplicables: number;
    preuvesValables: number;
    parCritere: { critere: number; titre: string; applicables: number; conformes: number }[];
  };
  criteres: { numero: number; titre: string; enonce: string; indicateurs: IndicateurQualiopi[] }[];
  preuves: PreuveQualiopi[];
  referentiel: { numero: number; titre: string; attendus: { code: string; libelle: string }[] }[];
  formats: { libelle: string; extensions: string[]; tailleMaxOctets: number };
  peutDeposer: boolean;
  peutDeclarer: boolean;
  dossierAudit: ReferentielDossierAudit;
}

export type FormatDossierAudit = 'ZIP' | 'PDF';
export type ChoixPeriodeAudit = 'DOUZE_MOIS' | 'ANNEE' | 'ANNEE_PRECEDENTE' | 'LIBRE';

/** Ce que la fenêtre du dossier d'audit propose, tel que l'API le rend (TF-432). */
export interface ReferentielDossierAudit {
  formats: { valeur: FormatDossierAudit; libelle: string; detail: string }[];
  periodes: { valeur: ChoixPeriodeAudit; libelle: string }[];
  periodeParDefaut: ChoixPeriodeAudit;
  tailleMaxOctets: number;
}

export interface DemandeDossierAudit {
  format: FormatDossierAudit;
  periode: ChoixPeriodeAudit;
  du?: string;
  au?: string;
  /** Vide : tous les indicateurs. */
  indicateurs: number[];
  site: string;
}

export interface ApercuDossierAudit {
  pieces: number;
  fichiers: number;
  liens: number;
  fusionnees: number;
  listees: number;
  octets: number;
  refus: string | null;
  periode: { choix: ChoixPeriodeAudit; du: string; au: string; arreteeAu: string; libelle: string; libelleActivite: string };
  indicateurs: number[];
  indicateursLibelle: string;
  antennes: string[];
  nomFichier: string;
}

export interface Rattachement {
  indicateur: number;
  attendu: string | null;
}

export interface EntreeDepotPreuve {
  titre: string;
  description: string;
  fichier: File | null;
  url: string;
  dateDocument: string;
  valableJusquAu: string;
  /** Code de l'antenne ; vide : tout l'organisme. */
  site: string;
  rattachements: Rattachement[];
}

const CLE = ['qualiopi'];

export function useQualiopi(site: string) {
  return useQuery({
    queryKey: [...CLE, site],
    queryFn: () => appelApi<ReponseQualiopi>(`/qualiopi?site=${encodeURIComponent(site)}`),
    placeholderData: keepPreviousData,
  });
}

/** Adresse du fichier d'une preuve : une route gardée, chaque lecture tracée. */
export function urlFichierPreuve(id: string, enLigne = true): string {
  return urlApi(`/qualiopi/preuves/${id}/fichier${enLigne ? '?inline=1' : ''}`);
}

function useApresGeste() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: CLE });
    // Le tableau de bord de la direction reprend le compte des indicateurs.
    void client.invalidateQueries({ queryKey: ['financements', 'tableau-de-bord-direction'] });
  };
}

export function useDeposerPreuve() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: (entree: EntreeDepotPreuve) => {
      const formulaire = new FormData();
      formulaire.append('titre', entree.titre);
      formulaire.append('description', entree.description);
      formulaire.append('dateDocument', entree.dateDocument);
      formulaire.append('valableJusquAu', entree.valableJusquAu);
      formulaire.append('site', entree.site);
      formulaire.append('rattachements', JSON.stringify(entree.rattachements));
      if (entree.fichier) formulaire.append('fichier', entree.fichier);
      else formulaire.append('url', entree.url);
      return appelApi<{ preuve: PreuveQualiopi }>('/qualiopi/preuves', { methode: 'POST', formulaire });
    },
    onSuccess: apres,
  });
}

export function useRattacherPreuve() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ id, rattachements }: { id: string; rattachements: Rattachement[] }) =>
      appelApi<{ preuve: PreuveQualiopi }>(`/qualiopi/preuves/${id}/rattachements`, {
        methode: 'PUT',
        corps: { rattachements },
      }),
    onSuccess: apres,
  });
}

export function useRetirerPreuve() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) =>
      appelApi<{ preuve: PreuveQualiopi }>(`/qualiopi/preuves/${id}/retrait`, { methode: 'POST', corps: { motif } }),
    onSuccess: apres,
  });
}

export function useDeclarerApplicabilite() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ numero, nonApplicable, motif }: { numero: number; nonApplicable: boolean; motif?: string }) =>
      appelApi<{ indicateur: number; nonApplicable: boolean }>(`/qualiopi/indicateurs/${numero}/applicabilite`, {
        methode: 'PUT',
        corps: nonApplicable ? { nonApplicable, motif } : { nonApplicable },
      }),
    onSuccess: apres,
  });
}

function corpsDossierAudit(demande: DemandeDossierAudit): Record<string, unknown> {
  return {
    ...demande,
    site: demande.site === TOUS_LES_SITES ? undefined : demande.site,
    ...(demande.periode === 'LIBRE' ? {} : { du: undefined, au: undefined }),
  };
}

/** Ce que le dossier d'audit contiendra : pièces, liens, poids et refus, sans rien lire des fichiers. */
export function useApercuDossierAudit(demande: DemandeDossierAudit, actif: boolean) {
  return useQuery({
    queryKey: [...CLE, 'dossier-audit', demande],
    queryFn: () =>
      appelApi<ApercuDossierAudit>('/qualiopi/dossier-audit/apercu', {
        methode: 'POST',
        corps: corpsDossierAudit(demande),
      }),
    enabled: actif,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/**
 * Télécharge le dossier d'audit. Il passe par `fetch` plutôt que par un lien :
 * la demande porte période et indicateurs, et un refus de l'API (dossier trop
 * lourd, période invalide) doit s'afficher à l'écran, pas s'enregistrer comme
 * un faux fichier.
 */
export async function telechargerDossierAudit(demande: DemandeDossierAudit): Promise<{ nomFichier: string }> {
  const reponse = await fetch(urlApi('/qualiopi/dossier-audit'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(corpsDossierAudit(demande)),
  });
  if (!reponse.ok) {
    const corps = (await reponse.json().catch(() => null)) as
      | { erreur?: { code?: string; message?: string; details?: unknown } }
      | null;
    if (reponse.status === 401) window.dispatchEvent(new Event(EVENEMENT_SESSION_EXPIREE));
    throw new ErreurApi(
      reponse.status,
      corps?.erreur?.code ?? 'ERREUR',
      corps?.erreur?.message ?? 'Le dossier d’audit n’a pas pu être produit.',
      corps?.erreur?.details,
    );
  }
  const nomFichier =
    /filename="([^"]+)"/.exec(reponse.headers.get('content-disposition') ?? '')?.[1] ??
    `dossier-audit-qualiopi.${demande.format === 'ZIP' ? 'zip' : 'pdf'}`;
  const url = URL.createObjectURL(await reponse.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  URL.revokeObjectURL(url);
  return { nomFichier };
}
